import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';

const TAGLINES = [
  'Celebrating Wellness',
  'Advancing Healthcare',
  'Trusted Formulations',
  'Science of Living Well',
];

export default function HeroSection() {
  const [taglineIndex, setTaglineIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setTaglineIndex((prev) => (prev + 1) % TAGLINES.length);
        setFade(true);
      }, 400);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-hero-gradient">
      {/* Background decorative shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-32 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white/5 rounded-full blur-2xl" />
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        {/* Floating circles */}
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full border border-white/10"
            style={{
              width: `${80 + i * 40}px`,
              height: `${80 + i * 40}px`,
              top: `${10 + i * 12}%`,
              right: `${5 + i * 8}%`,
              animation: `float ${4 + i}s ease-in-out infinite`,
              animationDelay: `${i * 0.5}s`,
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text Content */}
          <div>
            {/* Eyebrow */}
            {/* <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-xs font-semibold px-4 py-2 rounded-full mb-6 animate-fade-in">
              <span className="w-2 h-2 bg-accent rounded-full animate-pulse-slow" />
              
            </div> */}

            {/* Headline */}
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.08] mb-4">
              ANIR
              <br />
              <span className="text-accent">Healthcare</span>
            </h1>

            {/* Rotating tagline */}
            <p
              className={`font-display text-2xl sm:text-3xl font-semibold text-white/80 mb-6 min-h-[2.5rem] transition-all duration-400 ${
                fade ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
              }`}
            >
              {TAGLINES[taglineIndex]}
            </p>

            {/* Description */}
            <p className="text-blue-100 text-lg leading-relaxed mb-10 max-w-xl">
              A pharmaceutical company dedicated to offering the best finished formulations in the healthcare sector — improving the health of humanity with science, innovation, and care.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/products"
                className="inline-flex items-center justify-center gap-2 bg-white text-primary font-bold px-8 py-4 rounded-full shadow-hero hover:bg-blue-50 transition-all duration-300 hover:-translate-y-0.5 active:scale-95"
              >
                Explore Products
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/40 text-white font-bold px-8 py-4 rounded-full hover:bg-white/10 transition-all duration-300 active:scale-95"
              >
                Contact Us
              </Link>
            </div>

            {/* Stats row */}
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-sm">
              {[
                { value: '18+', label: 'Products' },
                { value: '10+', label: 'Categories' },
                { value: '100%', label: 'Quality' },
              ].map(({ value, label }) => (
                <div key={label} className="text-center">
                  <p className="font-display text-3xl font-bold text-white">{value}</p>
                  <p className="text-blue-300 text-xs font-medium mt-1">{label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Feature card + logo */}
          <div className="hidden lg:flex flex-col items-center justify-center gap-6 relative">
            {/* Main logo card */}
            <div className="relative animate-float">
              <div className="w-64 h-64 bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl flex items-center justify-center shadow-2xl">
                <img
                  src="/images/anir-logo.jpg"
                  alt="Anir Healthcare"
                  className="w-44 h-44 rounded-2xl object-cover shadow-xl"
                />
              </div>
              {/* Orbiting pill badges */}
              <div className="absolute -top-4 -right-4 bg-white rounded-xl px-3 py-2 shadow-lg text-xs font-bold text-primary flex items-center gap-1.5">
                <span className="text-base">💊</span> 18 Products
              </div>
              <div className="absolute -bottom-4 -left-4 bg-accent rounded-xl px-3 py-2 shadow-lg text-xs font-bold text-white flex items-center gap-1.5">
                <span className="text-base">🏆</span> Quality Assured
              </div>
              <div className="absolute top-1/2 -right-16 bg-medical-green rounded-xl px-3 py-2 shadow-lg text-xs font-bold text-white flex items-center gap-1.5 -translate-y-1/2">
                <span className="text-base">🌿</span> Wellness
              </div>
            </div>

            {/* Feature chips */}
            <div className="flex flex-wrap gap-3 justify-center max-w-xs">
              {['Nutrition', 'Bone Health', 'Antibiotics', 'Gastro', 'Vitamins', 'Pain Relief'].map((cat) => (
                <span key={cat} className="bg-white/10 border border-white/20 text-white/80 text-xs font-medium px-3 py-1.5 rounded-full">
                  {cat}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 80L60 70C120 60 240 40 360 33C480 27 600 33 720 40C840 47 960 53 1080 50C1200 47 1320 33 1380 27L1440 20V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z" fill="#f8faff" />
        </svg>
      </div>
    </section>
  );
}
